import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadIcon, SpinnerIcon } from '../../components/icons';
import Header from '../../components/Header';
import altasService, { errorMessage } from '../../services/altasService';
import {
  AltaSeguimiento,
  BajaReporte,
  DecisionRevisar,
  Ejecutiva,
  RevisionEnCurso,
  ReporteVentas,
  Vigencia,
} from '../../types/Altas';
import {
  Alert,
  Badge,
  BadgeTone,
  Button,
  Card,
  Panel,
  StatCard,
  staggerParent,
  enterChild,
} from '../../components/ui';
import {
  filtroClass,
  formatFecha,
  formatFechaSqlite,
  labelClass,
  periodoQuincenal,
  textoRevisar,
  thClass,
  ventasPorEjecutivo,
} from '../../components/altas/altasUi';
import Paginador from '../../components/altas/Paginador';

const POR_PAGINA = 25;
/** Hasta cuántas filas trae cada lista del reporte (MAX_FILAS_REPORTE del backend). */
const MAX_FILAS_LISTA = 2000;

const VIGENCIA_UI: Record<Vigencia, { label: string; tone: BadgeTone; title: string }> = {
  vigente: {
    label: 'Vigente',
    tone: 'positive',
    title: 'El plan sigue activo en Salesforce.',
  },
  no_vigente: {
    label: 'No vigente',
    tone: 'negative',
    title:
      'El plan vendido ya no está activo en Salesforce (baja, cancelado) y la persona no tiene otro plan pago. Un pago pendiente sigue vigente. Si el titular que se había ido solo del plan volvió a contratar, eso cancela su baja, no la de los demás miembros.',
  },
  sin_verificar: {
    label: 'Sin verificar',
    tone: 'neutral',
    title:
      'Salesforce no devolvió el estado actual de este plan, o devolvió uno que la app no conoce (se ve en "Estado en Salesforce"): no cuenta como vigente ni como baja. Se vuelve a consultar con el próximo "Calcular ventas" o con el cruce diario.',
  },
};

const PENDIENTE_DE_PAGO = 'Pendiente de pago';
// Estados con los que el plan vendido sigue en pie (los mismos que mira el backend).
const PLAN_SIGUE = new Set(['Activo', 'Pendiente activación downgrade', PENDIENTE_DE_PAGO]);

/**
 * Cómo se muestra la vigencia de una venta. Un plan con el pago pendiente
 * sigue vigente (la persona puede usar el plan), pero se muestra como tal.
 */
function vigenciaDe(a: AltaSeguimiento): {
  label: string;
  tone: BadgeTone;
  title: string;
} {
  if (a.vigencia === 'vigente' && a.estadoPlanSf === PENDIENTE_DE_PAGO) {
    return {
      label: PENDIENTE_DE_PAGO,
      tone: 'warning',
      title: 'El cobro falló o está pendiente, pero la persona sigue con el plan: cuenta como vigente.',
    };
  }
  return VIGENCIA_UI[a.vigencia];
}

/** Dispara la descarga de un archivo en el navegador. */
function guardarArchivo(archivo: Blob, nombre: string) {
  const url = URL.createObjectURL(archivo);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Pestaña "Seguimiento de altas": de las ventas confirmadas de un período,
 * cuáles siguen con el plan activo y cuáles de las anteriores se cayeron.
 * Sirve para comisionar a los ejecutivos. Nada se carga solo: se eligen
 * período y ejecutivo y "Calcular ventas" verifica con Salesforce y arma el
 * reporte (altas, por plan, ventas y bajas). Cambiar un filtro descarta lo
 * que se ve hasta volver a calcular; "Descargar Excel" baja lo calculado.
 */
const SeguimientoAltasPage: React.FC = () => {
  const [ejecutivas, setEjecutivas] = useState<Ejecutiva[]>([]);
  // Arranca con el período que se comisiona: del 16 del mes anterior al 15 de este.
  const [rangoInicial] = useState(() => periodoQuincenal(new Date()));
  const [desde, setDesde] = useState(rangoInicial.desde);
  const [hasta, setHasta] = useState(rangoInicial.hasta);
  const [filtroEjecutivo, setFiltroEjecutivo] = useState('');
  const [pagina, setPagina] = useState(1);
  // Error de lo último que se intentó, con un título que diga qué fue (cargar, descargar, decidir).
  const [error, setError] = useState<{ titulo: string; texto: string } | null>(null);
  // El reporte vale para los filtros con los que se calculó: cambiarlos lo descarta.
  const [reporte, setReporte] = useState<ReporteVentas | null>(null);
  const [calculando, setCalculando] = useState(false);
  // Número del último "Calcular ventas" pedido: si se cambió un filtro (o se pidió otro) mientras
  // se esperaba, la respuesta que llega es de otros filtros y se descarta.
  const ultimoPedido = useRef(0);
  // Aviso sobre el último "Calcular ventas": no se pudo, o se pudo pero con datos sin verificar.
  const [avisoReporte, setAvisoReporte] = useState<{ titulo: string; texto: string } | null>(null);
  const [descargando, setDescargando] = useState(false);
  // Venta y decisión ("Es venta" / "Ya tenía plan") en camino: el botón apretado muestra el
  // spinner, un doble clic no la manda dos veces y los botones de las demás esperan al recálculo.
  const [decidiendo, setDecidiendo] = useState<RevisionEnCurso | null>(null);
  // El estado tarda un render en verse: dos clics en el mismo instante lo pasarían. El ref no.
  const decidiendoRef = useRef(false);
  // Si Salesforce acaba de fallar, el backend pide esperar (429): los botones se
  // deshabilitan con una cuenta regresiva en vez de dejar que vuelva a rebotar.
  const [esperaSegundos, setEsperaSegundos] = useState(0);
  useEffect(() => {
    if (esperaSegundos <= 0) return undefined;
    const t = window.setTimeout(() => setEsperaSegundos((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [esperaSegundos]);

  useEffect(() => {
    altasService
      .listarEjecutivas()
      .then(setEjecutivas)
      .catch((err) => setError({ titulo: 'No se pudo cargar', texto: errorMessage(err, 'No se pudieron cargar los ejecutivos') }));
  }, []);

  // Cambiar un filtro descarta el reporte (y su página): lo que se ve es siempre de los filtros actuales.
  const cambiar = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPagina(1);
    setReporte(null);
    setAvisoReporte(null);
    ultimoPedido.current += 1;
    setCalculando(false);
  };

  const ejecutivoId = filtroEjecutivo ? Number(filtroEjecutivo) : undefined;
  // El nombre que encabeza el reporte sale del reporte, no del filtro: es de lo que se calculó.
  const nombreEjecutivo = (id: number | null | undefined) =>
    ejecutivas.find((e) => e.id === id)?.nombre ?? null;
  const reporteEjecutivo = nombreEjecutivo(reporte?.ejecutivaId);

  const calcularVentas = async () => {
    if (calculando || esperaSegundos > 0 || !desde || !hasta) return;
    const pedido = ultimoPedido.current + 1;
    ultimoPedido.current = pedido;
    setCalculando(true);
    setAvisoReporte(null);
    setError(null);
    try {
      const r = await altasService.calcularVentas(desde, hasta, ejecutivoId);
      if (pedido !== ultimoPedido.current) return;
      if (!r.success || !r.reporte) {
        if (r.retryAfter) setEsperaSegundos(r.retryAfter);
        setAvisoReporte(
          r.motivo === 'limite'
            // El límite de cálculos por minuto no es Salesforce caído: se dice tal cual.
            ? { titulo: 'Límite de cálculos por minuto', texto: r.error ?? 'Demasiados cálculos seguidos. Esperá un minuto y volvé a probar.' }
            : {
                titulo: reporte ? 'No se pudo volver a calcular' : 'No se pudo calcular',
                // Con espera, los segundos los cuenta el botón: el texto no los repite para que no queden desfasados.
                texto: r.retryAfter
                  ? 'Salesforce falló hace instantes. Esperá la cuenta regresiva para reintentar.'
                  : (r.error ?? 'No se pudo calcular las ventas'),
              },
        );
        return;
      }
      setReporte(r.reporte);
      setPagina(1);
      const { error: errorSf, esperar } = r.reporte.verificacion;
      if (esperar) {
        // La espera arrancó a mitad del cálculo: el reporte sale con lo último conocido y se cuenta igual.
        setEsperaSegundos(esperar);
        setAvisoReporte({
          titulo: 'Reporte con datos sin verificar',
          texto: 'Salesforce falló hace instantes y no se pudo verificar todo. El reporte muestra el último estado conocido de cada venta; esperá la cuenta regresiva para reintentar.',
        });
      } else if (errorSf) {
        setAvisoReporte({
          titulo: 'Reporte con datos sin verificar',
          texto: `No se pudo verificar todo con Salesforce (${errorSf}). El reporte muestra el último estado conocido de cada venta.`,
        });
      }
    } finally {
      if (pedido === ultimoPedido.current) setCalculando(false);
    }
  };
  // A mano no se calcula mientras una decisión está en camino: al terminar recalcula sola, y un
  // cálculo pedido en el medio podría volver antes de que la decisión se guarde y mostrarla sin ella.
  const calcular = () => {
    if (decidiendoRef.current) return;
    return calcularVentas();
  };

  // Después de una decisión, el reporte se refresca con lo último conocido: la decisión no
  // cambia nada en Salesforce, así que no se consulta ni se gasta el límite de "Calcular ventas".
  // La página se mantiene; la última verificación sigue siendo la del cálculo. Si se cambió
  // un filtro mientras viajaba (`pedido` ya no es el actual), lo que llega es de los filtros
  // viejos y se descarta, como con "Calcular ventas".
  const refrescarReporte = async (actual: ReporteVentas, pedido: number) => {
    const r = await altasService.verReporte(actual.periodo.desde, actual.periodo.hasta, actual.ejecutivaId ?? undefined);
    if (pedido !== ultimoPedido.current) return;
    if (!r.success || !r.reporte) {
      setError({ titulo: 'La decisión se guardó, pero no se pudo refrescar el reporte', texto: r.error ?? 'Volvé a calcular para verla.' });
      return;
    }
    setReporte({ ...r.reporte, verificacion: actual.verificacion });
  };

  // Baja lo que se ve: el período y el ejecutivo del reporte calculado.
  const descargar = async () => {
    if (descargando || !reporte) return;
    setDescargando(true);
    try {
      const r = await altasService.descargarReporte(
        reporte.periodo.desde,
        reporte.periodo.hasta,
        reporte.ejecutivaId ?? undefined,
      );
      if (!r.success || !r.archivo) {
        setError({ titulo: 'No se pudo descargar el Excel', texto: r.error ?? 'Probá de nuevo en un rato.' });
        return;
      }
      guardarArchivo(r.archivo, r.nombre ?? 'ventas.xlsx');
    } finally {
      setDescargando(false);
    }
  };

  // La app marcó la venta; el jefe decide acá mismo, que es donde comisiona. Después el
  // reporte se refresca sin Salesforce: la decisión cambia los totales, no el estado del plan.
  // Mientras hay un cálculo en curso no se decide: su respuesta pisaría el refresco.
  const sinRecalculo = calculando;
  const revisarAlta = async (alta: AltaSeguimiento, decision: DecisionRevisar) => {
    if (decidiendoRef.current || sinRecalculo || !reporte) return;
    decidiendoRef.current = true;
    setDecidiendo({ id: alta.id, decision });
    // Si se cambia un filtro mientras se guarda, el reporte ya se descartó: no se refresca
    // con los filtros viejos (quedaría el reporte viejo con el filtro nuevo en pantalla).
    const pedido = ultimoPedido.current;
    const actual = reporte;
    try {
      const res = await altasService.revisarAlta(alta.id, decision);
      if (!res.success) {
        setError({ titulo: 'No se pudo guardar la decisión', texto: res.error ?? 'Probá de nuevo en un rato.' });
        return;
      }
      if (pedido !== ultimoPedido.current) return;
      await refrescarReporte(actual, pedido);
    } finally {
      decidiendoRef.current = false;
      setDecidiendo(null);
    }
  };

  const ventas = useMemo(() => reporte?.ventas ?? [], [reporte]);
  const porEjecutivo = useMemo(() => ventasPorEjecutivo(reporte?.porPlan ?? []), [reporte]);
  // Las tarjetas vienen del backend, calculadas sobre todas las ventas del período (la lista se corta a 2000).
  const totales = reporte?.vigencias ?? { vigentes: 0, noVigentes: 0, sinVerificar: 0, paraRevisar: 0 };
  const total = ventas.length;
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  const paginaActual = Math.min(pagina, paginas);
  const primera = total === 0 ? 0 : (paginaActual - 1) * POR_PAGINA + 1;
  const ultima = Math.min(paginaActual * POR_PAGINA, total);
  const altas = ventas.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA);

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header />

      <main className="container mx-auto flex-grow px-4 py-8">
        <motion.div
          variants={staggerParent}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          <motion.div variants={enterChild} className="min-w-0">
            <h1 className="font-poppins text-2xl font-semibold text-gappex-secondary">
              Seguimiento de altas
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              De las ventas confirmadas, cuáles siguen con el plan activo en Salesforce y cuáles se
              dieron de baja.
            </p>
          </motion.div>

          <AnimatePresence initial={false}>
            {error && (
              <Alert
                key="error"
                tone="error"
                title={error.titulo}
                onClose={() => setError(null)}
              >
                {error.texto}
              </Alert>
            )}
          </AnimatePresence>

          <motion.div variants={enterChild}>
            <Card className="p-5">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label htmlFor="seg-desde" className={labelClass}>
                    Venta desde
                  </label>
                  <input
                    id="seg-desde"
                    type="date"
                    value={desde}
                    max={hasta || undefined}
                    onChange={(e) => cambiar(setDesde)(e.target.value)}
                    className={filtroClass}
                  />
                </div>
                <div>
                  <label htmlFor="seg-hasta" className={labelClass}>
                    Venta hasta
                  </label>
                  <input
                    id="seg-hasta"
                    type="date"
                    value={hasta}
                    min={desde || undefined}
                    onChange={(e) => cambiar(setHasta)(e.target.value)}
                    className={filtroClass}
                  />
                </div>
                <div>
                  <label htmlFor="seg-ejecutivo" className={labelClass}>
                    Ejecutivo
                  </label>
                  <select
                    id="seg-ejecutivo"
                    value={filtroEjecutivo}
                    onChange={(e) => cambiar(setFiltroEjecutivo)(e.target.value)}
                    className={filtroClass}
                  >
                    <option value="">Todos</option>
                    {ejecutivas.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="ms-auto flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={calcular}
                    disabled={calculando || esperaSegundos > 0 || !desde || !hasta || decidiendo !== null}
                    icon={calculando ? <SpinnerIcon /> : undefined}
                    title="Verifica con Salesforce las ventas del período y las anteriores, y arma el reporte de altas y bajas"
                  >
                    <span className="tabular-nums">
                      {calculando
                        ? 'Calculando…'
                        : esperaSegundos > 0
                          ? `Calcular ventas (${esperaSegundos} s)`
                          : 'Calcular ventas'}
                    </span>
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={descargar}
                    disabled={!reporte || descargando}
                    icon={descargando ? <SpinnerIcon /> : <DownloadIcon />}
                    title={
                      reporte
                        ? 'Descarga el reporte en Excel: Resumen, Ventas y Bajas'
                        : 'Primero calculá las ventas'
                    }
                  >
                    Descargar Excel
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>

          <AnimatePresence initial={false}>
            {avisoReporte && (
              <Alert
                key="reporte"
                tone="warning"
                title={avisoReporte.titulo}
                onClose={() => setAvisoReporte(null)}
                action={
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={calcular}
                    disabled={calculando || esperaSegundos > 0 || decidiendo !== null}
                  >
                    <span className="tabular-nums">
                      {esperaSegundos > 0 ? `Reintentar en ${esperaSegundos} s` : 'Reintentar'}
                    </span>
                  </Button>
                }
              >
                {avisoReporte.texto}
              </Alert>
            )}
            {reporte?.truncado && (
              <Alert key="truncado" tone="warning" title="Las listas se cortaron">
                Hay {reporte.filas.ventas} ventas y {reporte.filas.bajas} bajas; las listas muestran
                las primeras {MAX_FILAS_LISTA} de cada una. Los totales, las tarjetas y "Ventas por
                plan" están completos. Achicá el período o elegí un ejecutivo para ver todas las filas.
              </Alert>
            )}
          </AnimatePresence>

          {reporte && (
            <motion.div variants={enterChild} className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <StatCard
                  label="Altas"
                  value={String(reporte.totales.altas)}
                  tone="positive"
                  sub={`Ventas confirmadas del ${formatFecha(reporte.periodo.desde)} al ${formatFecha(reporte.periodo.hasta)}`}
                  title="Cuenta planes, como el dashboard: una venta × 3 suma 3. Una venta del período que ya se dio de baja suma acá y en Bajas. Si el titular se fue y el plan sigue, la venta no cambia de número."
                />
                <StatCard
                  label="Bajas"
                  value={String(reporte.totales.bajas)}
                  tone="negative"
                  sub={
                    reporteEjecutivo
                      ? `Ventas de "${reporteEjecutivo}" que se dieron de baja en este período`
                      : 'Ventas que se dieron de baja en el período'
                  }
                  title="Cuenta planes. Un pago pendiente o quien volvió a contratar otro plan pago no es baja. El titular que se va de un plan que sigue activo cuenta 1; los demás se descuentan cuando se caigan."
                />
              </div>

              <Panel
                title="Ventas por plan"
                subtitle={`${reporteEjecutivo ?? 'Todos los ejecutivos'} · ${formatFecha(reporte.periodo.desde)} al ${formatFecha(reporte.periodo.hasta)}`}
              >
                {reporte.porPlan.length === 0 ? (
                  <p className="py-4 text-center text-sm text-slate-400">
                    No hay ventas confirmadas en el período.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm sm:w-auto sm:min-w-[28rem]">
                      <thead>
                        <tr className="border-b border-slate-200 text-left">
                          <th className={`${thClass} pe-6`}>Ejecutivo</th>
                          {porEjecutivo.planes.map((plan) => (
                            <th key={plan} className={`${thClass} pe-6 text-right`}>
                              {plan}
                            </th>
                          ))}
                          <th className={`${thClass} text-right`}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {porEjecutivo.filas.map((f) => (
                          <tr key={f.ejecutivaId} className="border-b border-slate-100 last:border-0">
                            <td className="py-1.5 pe-6 font-medium text-gappex-secondary">{f.ejecutiva}</td>
                            {porEjecutivo.planes.map((plan) => (
                              <td key={plan} className="py-1.5 pe-6 text-right tabular-nums text-slate-700">
                                {f.porPlan[plan] ?? 0}
                              </td>
                            ))}
                            <td className="py-1.5 text-right font-semibold tabular-nums text-slate-700">
                              {f.total}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            </motion.div>
          )}

          {!reporte && !calculando && (
            <motion.div variants={enterChild}>
              <Card className="p-6 text-center text-sm text-slate-400">
                Elegí el período y el ejecutivo y apretá "Calcular ventas".
              </Card>
            </motion.div>
          )}

          {reporte && (
            <motion.div variants={enterChild} className="grid gap-4 sm:grid-cols-2">
              <StatCard
                label="Vigentes"
                value={String(totales.vigentes)}
                tone="positive"
                title="Cuenta planes, como el dashboard: una venta × 3 suma 3."
              />
              <StatCard
                label="No vigentes"
                value={String(totales.noVigentes)}
                tone="negative"
                title="Cuenta planes, como el dashboard: una venta × 3 suma 3."
              />
              {totales.paraRevisar > 0 && (
                <StatCard
                  label="Para revisar"
                  value={String(totales.paraRevisar)}
                  tone="warning"
                  title="Ventas con algo para mirar antes de comisionar: ya tenían un plan, o el plan empezó mucho antes de cargarlas. Están contadas arriba hasta que decidas."
                />
              )}
            </motion.div>
          )}

          {reporte && (
            <motion.div variants={enterChild}>
              <Panel
                title="Ventas confirmadas"
                subtitle={`${total} ${total === 1 ? 'alta' : 'altas'}${
                  total > POR_PAGINA ? ` · mostrando ${primera} a ${ultima}` : ''
                }${totales.sinVerificar > 0 ? ` · ${totales.sinVerificar} sin verificar todavía` : ''}`}
              >
                {altas.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-400">
                    No hay ventas confirmadas con estos filtros.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-left">
                          <th className={`${thClass} pe-4`}>Ejecutivo</th>
                          <th className={`${thClass} pe-4`}>DNI</th>
                          <th className={`${thClass} pe-4`}>Nombre</th>
                          <th className={`${thClass} pe-4`}>Plan</th>
                          <th className={`${thClass} pe-4`}>Vigencia</th>
                          <th className={`${thClass} pe-4`}>Fecha de venta</th>
                          <th className={`${thClass} pe-4`}>Fecha de baja</th>
                          <th className={`${thClass} pe-4`}>Verificado</th>
                          <th className={thClass}>Estado en Salesforce</th>
                        </tr>
                      </thead>
                      <tbody>
                        {altas.map((a: AltaSeguimiento) => {
                          const vigencia = vigenciaDe(a);
                          const revisar = textoRevisar(a);
                          return (
                            <React.Fragment key={a.id}>
                              <tr
                                className={`transition-colors duration-100 ease-out hover:bg-slate-50/70 ${
                                  revisar
                                    ? 'bg-amber-50/40'
                                    : 'border-b border-slate-100 last:border-0'
                                }`}
                              >
                                <td className="py-2 pe-4 font-medium text-gappex-secondary">
                                  {a.ejecutiva}
                                </td>
                                <td className="py-2 pe-4 tabular-nums text-slate-700">{a.dni}</td>
                                <td className="py-2 pe-4 text-slate-700">{a.nombre ?? '—'}</td>
                                <td className="py-2 pe-4 text-slate-700">
                                  {a.planSf ?? a.planCargado}
                                  {(a.cantidadPlanes ?? 1) > 1 && (
                                    <span
                                      className="ms-1.5 tabular-nums text-slate-400"
                                      title={`Plan para ${a.cantidadPlanes} miembros: cuenta como ${a.cantidadPlanes} ventas`}
                                    >
                                      × {a.cantidadPlanes}
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 pe-4">
                                  <Badge tone={vigencia.tone} title={vigencia.title}>
                                    {vigencia.label}
                                  </Badge>
                                  {a.bajaTitular && a.vigencia === 'vigente' && (
                                    <Badge
                                      tone="warning"
                                      className="ms-1.5"
                                      title={`El titular se dio de baja el ${formatFecha(a.bajaTitular.fecha)}${
                                        a.bajaTitular.estimada ? ' (día en que la app lo vio)' : ''
                                      }${
                                        a.recontrato
                                          ? ', pero volvió a contratar: su baja no cuenta.'
                                          : ': cuenta 1 baja en ese período.'
                                      }${
                                        PLAN_SIGUE.has(a.estadoPlanSf ?? '')
                                          ? ' El plan sigue activo para los demás miembros, que siguen vigentes.'
                                          : ''
                                      }`}
                                    >
                                      Titular de baja
                                    </Badge>
                                  )}
                                  {a.recontrato && (
                                    <Badge
                                      tone="neutral"
                                      className="ms-1.5"
                                      title={
                                        a.bajaTitular
                                          // La recontratación es del titular que se fue solo: cancela su baja, no la de los demás.
                                          ? `El titular, que se dio de baja el ${formatFecha(a.bajaTitular.fecha)}, hoy tiene ${a.recontrato.plan}${
                                              a.recontrato.desde ? ` desde el ${formatFecha(a.recontrato.desde)}` : ''
                                            }: su baja no cuenta. Los demás miembros siguen el estado del plan vendido.`
                                          : `El plan vendido ya no está activo, pero hoy tiene ${a.recontrato.plan}${
                                              a.recontrato.desde ? ` desde el ${formatFecha(a.recontrato.desde)}` : ''
                                            }: sigue vigente y no es baja`
                                      }
                                    >
                                      Volvió a contratar · {a.recontrato.plan}
                                    </Badge>
                                  )}
                                  {revisar && (
                                    <Badge
                                      tone="warning"
                                      className="ms-1.5"
                                      title="Hay algo para mirar antes de comisionarla"
                                    >
                                      Revisar
                                    </Badge>
                                  )}
                                </td>
                                <td className="py-2 pe-4 tabular-nums text-slate-700">
                                  {formatFecha(a.fechaVenta)}
                                </td>
                                <td className="py-2 pe-4 tabular-nums text-slate-700">
                                  {formatFecha(a.fechaBajaSf ?? null)}
                                </td>
                                <td className="py-2 pe-4 tabular-nums text-slate-400">
                                  {formatFechaSqlite(a.estadoPlanSfAt ?? null)}
                                </td>
                                <td className="py-2 text-slate-700">{a.estadoPlanSf ?? '—'}</td>
                              </tr>
                              {revisar && (
                                <tr className="border-b border-slate-100 bg-amber-50/40 last:border-0">
                                  <td
                                    colSpan={9}
                                    className="px-3 pb-2.5 pt-0.5 text-xs text-amber-800"
                                  >
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                      <span>{revisar}</span>
                                      <span className="flex gap-1.5">
                                        <Button
                                          type="button"
                                          variant="secondary"
                                          className="!py-1 !text-xs"
                                          title={
                                            sinRecalculo
                                              ? 'Esperá a que termine el cálculo para decidir'
                                              : 'Es una venta del ejecutivo: se comisiona'
                                          }
                                          disabled={decidiendo !== null || sinRecalculo}
                                          icon={decidiendo?.id === a.id && decidiendo.decision === 'venta' ? <SpinnerIcon /> : undefined}
                                          onClick={() => revisarAlta(a, 'venta')}
                                        >
                                          Es venta
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="secondary"
                                          className="!py-1 !text-xs"
                                          title={
                                            sinRecalculo
                                              ? 'Esperá a que termine el cálculo para decidir'
                                              : 'Ya tenía el plan: pasa a "Ya era cliente" y sale del seguimiento'
                                          }
                                          disabled={decidiendo !== null || sinRecalculo}
                                          icon={decidiendo?.id === a.id && decidiendo.decision === 'ya_tenia_plan' ? <SpinnerIcon /> : undefined}
                                          onClick={() => revisarAlta(a, 'ya_tenia_plan')}
                                        >
                                          Ya tenía plan
                                        </Button>
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <Paginador pagina={paginaActual} paginas={paginas} onPagina={setPagina} />
              </Panel>
            </motion.div>
          )}

          {reporte && (
            <motion.div variants={enterChild}>
              <Panel
                title="Bajas"
                subtitle={`Ventas de cualquier fecha cuyo plan se dio de baja entre el ${formatFecha(reporte.periodo.desde)} y el ${formatFecha(reporte.periodo.hasta)}. Un pago pendiente o quien volvió a contratar otro plan pago no es baja.`}
              >
                {reporte.bajas.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-400">
                    No hay bajas en el período.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-left">
                          <th className={`${thClass} pe-4`}>Ejecutivo</th>
                          <th className={`${thClass} pe-4`}>DNI</th>
                          <th className={`${thClass} pe-4`}>Nombre</th>
                          <th className={`${thClass} pe-4`}>Plan</th>
                          <th className={`${thClass} pe-4`}>Fecha de venta</th>
                          <th className={`${thClass} pe-4`}>Fecha de baja</th>
                          <th className={thClass}>Estado en Salesforce</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reporte.bajas.map((b: BajaReporte) => (
                          // Una venta puede tener dos filas: la baja del titular y la del resto del plan.
                          <tr key={`${b.id}-${b.soloTitular ? 'titular' : 'plan'}`} className="border-b border-slate-100 last:border-0">
                            <td className="py-2 pe-4 font-medium text-gappex-secondary">{b.ejecutiva}</td>
                            <td className="py-2 pe-4 tabular-nums">{b.dni}</td>
                            <td className="py-2 pe-4">{b.nombre ?? '—'}</td>
                            <td className="py-2 pe-4">
                              {b.planSf ?? b.planCargado}
                              {(b.cantidadPlanes ?? 1) > 1 && (
                                <span
                                  className="ms-1.5 tabular-nums text-slate-400"
                                  title={`Cuenta como ${b.cantidadPlanes} bajas: los miembros del plan que no se habían descontado antes`}
                                >
                                  × {b.cantidadPlanes}
                                </span>
                              )}
                              {b.soloTitular && (
                                <Badge
                                  tone="neutral"
                                  className="ms-1.5"
                                  title="Solo se dio de baja el titular (cuenta 1); el plan siguió para los demás miembros, que se descuentan aparte si después se cae"
                                >
                                  Solo el titular
                                </Badge>
                              )}
                            </td>
                            <td className="py-2 pe-4 tabular-nums">{formatFecha(b.fechaVenta)}</td>
                            <td
                              className="py-2 pe-4 tabular-nums"
                              title={
                                b.fechaBajaEstimada
                                  ? 'Salesforce no tiene fecha de baja: es el día en que la app vio el plan sin estar activo'
                                  : undefined
                              }
                            >
                              {formatFecha(b.fechaBaja)}
                              {b.fechaBajaEstimada && (
                                <span className="ms-1 text-slate-400">*</span>
                              )}
                            </td>
                            <td className="py-2">{b.estadoPlanSf ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {reporte.bajas.some((b) => b.fechaBajaEstimada) && (
                      <p className="mt-2 text-xs text-slate-400">
                        * Salesforce no tiene fecha de baja: es el día en que la app vio el plan sin
                        estar activo.
                      </p>
                    )}
                  </div>
                )}
              </Panel>
            </motion.div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default SeguimientoAltasPage;
